import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'
import AssetDownload from '@comp/product/asset-download'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  badges: string[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  howEyebrow: string
  howTitle: string
  steps: Step[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Privileged access for DevOps & platform teams',
    metaDescription:
      'Monopam brokers Kubernetes, database, SSH, and RDP access without changing the tools engineers love: short-lived kubeconfigs, vault-injected database credentials, agentless session recording, and secrets with a lifecycle.',
    heroEyebrow: 'Use cases · DevOps privileged access',
    heroTitle: ['PAM that speaks kubectl.', 'And psql. And SSH.'],
    heroBody:
      'Platform and DevOps teams carry the most dangerous credentials in the company (cluster-admin kubeconfigs in dotfiles, shared database passwords in team vault docs, root SSH keys in CI variables), and classic PAM tools do not speak their protocols. Monopam brokers it all, through the tools engineers already use.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    badges: [
      'Native kubectl, psql, SSH',
      'Zero standing privilege',
      'Session recording built in',
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'Broker the access. Keep the workflow.',
    pillarsBody:
      'Engineers do not adopt PAM that breaks their tools. Monopam sits in the path (kubectl, psql, DBeaver, SSH) and adds policy, recording, and expiry without changing a single habit.',
    pillars: [
      {
        title: 'Kubernetes and OpenShift, brokered',
        description:
          'Cluster access becomes brokered and temporary: engineers keep kubectl and oc, the cluster stops keeping standing admins.',
        bullets: [
          'Short-lived kubeconfigs instead of standing cluster-admin',
          'Policy by verb, namespace, and resource',
          'JIT tokens: zero standing privilege',
          'kubectl exec recording and native oc login',
        ],
      },
      {
        title: 'Databases with their own tools',
        description:
          'Engineers connect with the clients they already use; Monopam sits in between with credentials, policy, and audit.',
        bullets: [
          'psql, DBeaver, and SSMS through the proxy',
          'Vault-injected credentials the user never sees',
          'SQL-level policy and audit',
          'Dynamic data masking for PII',
        ],
      },
      {
        title: 'Servers and network gear, agentless',
        description:
          'The gateway brings recorded, policy-controlled sessions to anything with an address; no agents on the targets.',
        bullets: [
          'Browser-based RDP, SSH, VNC, and Telnet via the gateway',
          'Command filtering on privileged sessions',
          'Clipboard and file-transfer policy',
          'Full session recording and playback',
        ],
      },
      {
        title: 'Secrets with a lifecycle',
        description:
          'Everything privileged lives in the vault and expires on its own, and approvals move fast enough that nobody routes around them.',
        bullets: [
          'Vaulted credentials and SSH keys',
          'Automatic rotation on schedule or check-in',
          'Time-bound checkout with auto-revoke',
          'Approval flows that reach Slack-speed',
        ],
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'Vault it. Broker it. Let it expire.',
    steps: [
      {
        title: 'Vault',
        description:
          'Move the kubeconfigs, database passwords, and SSH keys out of dotfiles and CI variables and into the vault, with owners and rotation.',
      },
      {
        title: 'Broker',
        description:
          'Route kubectl, psql, and SSH through Monopam: policy on every verb, query, and command, recording on every session.',
      },
      {
        title: 'Expire',
        description:
          'Access is time-bound by default: checkouts auto-revoke, JIT tokens expire, and standing privilege trends toward zero.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'The capabilities behind this use case.',
    related: [
      {
        label: 'Kubernetes & OpenShift',
        description: 'Short-lived kubeconfigs, JIT tokens, exec recording.',
        href: '/pam/kubernetes',
      },
      {
        label: 'Database access',
        description: 'Brokered psql, DBeaver, and SSMS with SQL-level policy.',
        href: '/pam/database-access',
      },
      {
        label: 'Monopam Gateway',
        description: 'Agentless RDP, SSH, and VNC: recorded, in the browser.',
        href: '/pam/gateway',
      },
    ],
  },
  tr: {
    metaTitle: 'DevOps ve platform ekipleri için ayrıcalıklı erişim',
    metaDescription:
      'Monopam, mühendislerin sevdiği araçları değiştirmeden Kubernetes, veritabanı, SSH ve RDP erişimini aracılar: kısa ömürlü kubeconfig’ler, kasadan enjekte edilen veritabanı kimlik bilgileri, ajansız oturum kaydı ve yaşam döngüsü olan sırlar.',
    heroEyebrow: 'Kullanım senaryoları · DevOps ayrıcalıklı erişimi',
    heroTitle: ['kubectl konuşan bir PAM.', 'psql de. SSH de.'],
    heroBody:
      'Şirketin en tehlikeli kimlik bilgilerini platform ve DevOps ekipleri taşır: dotfile’lardaki cluster-admin kubeconfig’ler, ekip dokümanlarındaki ortak veritabanı parolaları, CI değişkenlerindeki root SSH anahtarları. Klasik PAM araçları ise bu ekiplerin protokollerini konuşmaz. Monopam hepsini, mühendislerin zaten kullandığı araçlar üzerinden aracılar.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    badges: [
      'Doğal kubectl, psql, SSH',
      'Sıfır kalıcı ayrıcalık',
      'Yerleşik oturum kaydı',
    ],
    pillarsEyebrow: 'Ne elde edersiniz',
    pillarsTitle: 'Erişimi aracılayın. İş akışını koruyun.',
    pillarsBody:
      'Mühendisler araçlarını bozan PAM’i benimsemez. Monopam yolun üzerine oturur (kubectl, psql, DBeaver, SSH) ve tek bir alışkanlığı değiştirmeden politika, kayıt ve süre sınırı ekler.',
    pillars: [
      {
        title: 'Kubernetes ve OpenShift, aracılı',
        description:
          'Küme erişimi aracılı ve geçici hale gelir: mühendisler kubectl ve oc ile çalışmaya devam eder, kümede kalıcı admin kalmaz.',
        bullets: [
          'Kalıcı cluster-admin yerine kısa ömürlü kubeconfig’ler',
          'Verb, namespace ve resource bazında politika',
          'JIT token’lar: sıfır kalıcı ayrıcalık',
          'kubectl exec kaydı ve doğal oc login',
        ],
      },
      {
        title: 'Veritabanları, kendi araçlarıyla',
        description:
          'Mühendisler zaten kullandıkları istemcilerle bağlanır; arada kimlik bilgisi, politika ve denetimle Monopam durur.',
        bullets: [
          'Proxy üzerinden psql, DBeaver ve SSMS',
          'Kullanıcının hiç görmediği, kasadan enjekte edilen kimlik bilgileri',
          'SQL seviyesinde politika ve denetim',
          'PII için dinamik veri maskeleme',
        ],
      },
      {
        title: 'Sunucular ve ağ cihazları, ajansız',
        description:
          'Gateway, adresi olan her şeye kayıtlı ve politika kontrollü oturum getirir; hedeflere ajan kurulmaz.',
        bullets: [
          'Gateway ile tarayıcıdan RDP, SSH, VNC ve Telnet',
          'Ayrıcalıklı oturumlarda komut filtreleme',
          'Pano ve dosya aktarımı politikası',
          'Tam oturum kaydı ve oynatma',
        ],
      },
      {
        title: 'Yaşam döngüsü olan sırlar',
        description:
          'Ayrıcalıklı olan her şey kasada yaşar ve kendi kendine sona erer; onaylar ise kimsenin etrafından dolanmayacağı kadar hızlı akar.',
        bullets: [
          'Kasalanmış kimlik bilgileri ve SSH anahtarları',
          'Zamanlamayla veya iadede otomatik rotasyon',
          'Otomatik iptalli, süre sınırlı checkout',
          'Slack hızına ulaşan onay akışları',
        ],
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Kasaya alın. Aracılayın. Süresi dolsun.',
    steps: [
      {
        title: 'Kasaya alın',
        description:
          'kubeconfig’leri, veritabanı parolalarını ve SSH anahtarlarını dotfile’lardan ve CI değişkenlerinden çıkarın; sahipli ve rotasyonlu olarak kasaya taşıyın.',
      },
      {
        title: 'Aracılayın',
        description:
          'kubectl’i, psql’i ve SSH’i Monopam’den geçirin: her verb, sorgu ve komutta politika, her oturumda kayıt.',
      },
      {
        title: 'Süresini sınırlayın',
        description:
          'Erişim varsayılan olarak süre sınırlıdır: checkout’lar kendiliğinden iptal olur, JIT token’lar dolar, kalıcı ayrıcalık sıfıra iner.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'Bu senaryonun arkasındaki yetenekler.',
    related: [
      {
        label: 'Kubernetes & OpenShift',
        description: 'Kısa ömürlü kubeconfig’ler, JIT token’lar, exec kaydı.',
        href: '/pam/kubernetes',
      },
      {
        label: 'Veritabanı erişimi',
        description: 'SQL seviyesinde politikayla aracılı psql, DBeaver ve SSMS.',
        href: '/pam/database-access',
      },
      {
        label: 'Monopam Gateway',
        description: 'Ajansız RDP, SSH ve VNC: tarayıcıda, kayıt altında.',
        href: '/pam/gateway',
      },
    ],
  },
}

type Props = { locale: string }

export default function DevopsPrivilegedAccessPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/solutions/devops-privileged-access"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={<>{c.pillarsTitle}</>}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <ProductHowItWorks
          eyebrow={c.howEyebrow}
          title={<>{c.howTitle}</>}
          steps={c.steps}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <AssetDownload
          asset="k8s-guide"
          title={{
            en: 'Securing Kubernetes access: the field guide.',
            tr: 'Kubernetes erişim güvenliği: saha kılavuzu.',
          }}
          copy={{
            en: 'From kubeconfig sprawl to zero standing privilege in three stages, with checklists, an OpenShift note, and the five questions to ask any vendor.',
            tr: 'Kubeconfig dağınıklığından sıfır kalıcı ayrıcalığa üç aşama: kontrol listeleri, OpenShift notu ve her tedarikçiye sorulacak beş soruyla.',
          }}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
